import { Alert, Badge, cn, formatMNT } from "@artisan/ui";
import {
  Box,
  Camera,
  Check,
  ChevronLeft,
  Expand,
  Image as ImageIcon,
  ImagePlus,
  Images,
  Loader2,
  Maximize,
  MessageSquareText,
  Minus,
  Package,
  Plus,
  Sparkles,
  TriangleAlert,
  X,
  Zap,
} from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router";

import { ApiError, describeError } from "../lib/api";
import { nowIso } from "../lib/time";
import { projectTitle } from "../project/actions";
import { nextId, type PhotoRef, type RenderItemDraft, type RenderResultRow } from "../project/model";
import { preparePhoto, storePhoto } from "../project/photos";
import { photoBlob, updateProject, useProject } from "../project/store";
import { currentEngine, EnginePicker, engineLabel, useEngines } from "../render/engine-picker";
import { CatalogTab, isSetupError, PlacedList, SetupNotice } from "../render/catalog-panel";
import { PhotoCanvas } from "../render/photo-canvas";
import { draftFromAsset, failureText, isRunning, MAX_ITEMS, photoAspect, refreshResults, startRender } from "../render/scene";

/** An object URL for a stored photo. Keyed by the photo's hash, not the object: every edit
 * clones the project, and a new URL each time would remount the canvas mid-drag. */
function useBlobUrl(photo: PhotoRef | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const sha256 = photo?.sha256;
  useEffect(() => {
    let revoke: string | null = null;
    let live = true;
    if (sha256)
      void photoBlob({ sha256 }).then((b) => {
        if (!b || !live) return;
        revoke = URL.createObjectURL(b);
        setUrl(revoke);
      });
    return () => {
      live = false;
      if (revoke) URL.revokeObjectURL(revoke);
      setUrl(null);
    };
  }, [sha256]);
  return url;
}

// Node canvas geometry (canvas units; the view pans and zooms over it).
const TOP = 132;
const LEFT = 32;
const HEAD = 34;
const SRC_FOOT = 50;
const OUT_W = 200;
const OUT_FOOT = 44;
const GAP = 20;
const PER_COLUMN = 3;

function layout(aspect: number, results: number) {
  const imgW = aspect >= 1 ? 400 : Math.max(260, Math.round(470 * aspect));
  const imgH = Math.round(imgW / aspect);
  const src = { x: LEFT, y: TOP, w: imgW + 12, h: HEAD + imgH + SRC_FOOT, imgH };
  const outImgH = Math.round((OUT_W - 12) / aspect);
  const outH = HEAD + outImgH + OUT_FOOT;
  const outs = Array.from({ length: results }, (_, i) => ({
    x: src.x + src.w + 64 + Math.floor(i / PER_COLUMN) * (OUT_W + 40),
    y: TOP - 24 + (i % PER_COLUMN) * (outH + GAP),
    w: OUT_W,
    h: outH,
    imgH: outImgH,
  }));
  const right = Math.max(src.x + src.w, ...outs.map((o) => o.x + o.w));
  const bottom = Math.max(src.y + src.h, ...outs.map((o) => o.y + o.h));
  return { src, outs, right, bottom };
}

function Node({
  className,
  style,
  label,
  icon: Icon,
  title,
  meta,
  children,
  inPort,
  outPort,
  selected,
}: {
  className?: string;
  style: React.CSSProperties;
  label: string;
  icon: typeof ImageIcon;
  title: string;
  meta?: string;
  children: React.ReactNode;
  inPort?: boolean;
  outPort?: boolean;
  selected?: boolean;
}) {
  return (
    <article
      aria-label={label}
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(
        "absolute rounded-[14px] border bg-card shadow-[0_1px_2px_#0000000f,0_8px_24px_#0000001a]",
        selected && "border-chart-1 shadow-[0_0_0_2px_var(--chart-1),0_10px_30px_#0000002e]",
        className,
      )}
      style={style}
    >
      <div className="flex h-[34px] items-center gap-1.5 px-2.5 text-xs font-semibold whitespace-nowrap">
        <Icon className="size-3.5 text-muted-foreground" aria-hidden />
        {title}
        <span className="flex-1" />
        {meta ? <small className="text-[11px] font-medium text-muted-foreground">{meta}</small> : null}
      </div>
      {children}
      {inPort ? <span className="absolute top-1/2 -left-[7px] -mt-1.5 size-3 rounded-full border-2 border-foreground/45 bg-card" /> : null}
      {outPort ? (
        <span className={cn("absolute top-1/2 -right-[7px] -mt-1.5 size-3 rounded-full border-2 bg-card", selected ? "border-chart-1" : "border-foreground/45")} />
      ) : null}
    </article>
  );
}

function ResultNode({
  row,
  n,
  at,
  base,
  approved,
  onOpen,
  onApprove,
}: {
  row: RenderResultRow;
  n: number;
  at: { x: number; y: number; w: number; imgH: number };
  base: string | null;
  approved: boolean;
  onOpen: () => void;
  onApprove: () => void;
}) {
  const running = isRunning(row);
  const ok = row.status === "succeeded" && row.output_url;
  return (
    <Node
      label={`Рендер ${n}${running ? ", үүсгэж байна" : ok ? "" : ", бүтсэнгүй"}`}
      icon={Sparkles}
      title={`Рендер ${n}`}
      meta={row.quality === "final" ? "Чанартай" : "Хурдан"}
      inPort
      selected={approved}
      style={{ left: at.x, top: at.y, width: at.w }}
    >
      <div className="relative mx-1.5 overflow-hidden rounded-[9px] bg-muted" style={{ height: at.imgH }}>
        {ok ? (
          <button type="button" className="block size-full" onClick={onOpen} aria-label={`Рендер ${n}-ийг томоор харах`}>
            <img src={row.output_url ?? ""} alt={`AI рендер ${n}`} className="size-full object-cover" />
            <span className="absolute right-1.5 bottom-1.5 grid size-7 place-items-center rounded-md bg-[#0b0b0d]/70 text-white">
              <Expand className="size-3.5" aria-hidden />
            </span>
          </button>
        ) : (
          <>
            {base ? <img src={base} alt="" className="size-full object-cover" /> : null}
            <div className="absolute inset-0 grid place-content-center place-items-center gap-2 bg-card/60 text-xs font-semibold backdrop-blur-md">
              {running ? (
                <>
                  <Loader2 className="size-5 animate-spin text-chart-1" aria-hidden />
                  {row.status === "queued" ? "Дараалалд" : "Үүсгэж байна"}
                  <span className="h-1 w-[70px] overflow-hidden rounded-sm bg-muted">
                    <span className="block h-full bg-chart-1 transition-[width]" style={{ width: `${Math.max(8, row.progress ?? 0)}%` }} />
                  </span>
                </>
              ) : (
                <>
                  <TriangleAlert className="size-5 text-destructive" aria-hidden />
                  <span className="max-w-[160px] text-center leading-4 font-medium">{failureText(row.error_code)}</span>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <div className="flex h-11 items-center justify-between gap-2 px-2.5 text-[11px] whitespace-nowrap text-muted-foreground">
        <span>
          {row.items ?? "?"} зүйл{row.seconds ? ` · ${row.seconds} сек` : ""}
        </span>
        {ok ? (
          approved ? (
            <Badge variant="success" icon={Check}>
              Батлагдсан
            </Badge>
          ) : (
            <button type="button" onClick={onApprove} className="inline-flex h-8 items-center gap-1 rounded-md border px-2 font-semibold text-foreground hover:bg-accent">
              <Check className="size-3.5" aria-hidden /> Батлах
            </button>
          )
        ) : running ? (
          <b className="font-semibold text-foreground tabular">{row.progress ?? 0}%</b>
        ) : null}
      </div>
    </Node>
  );
}

function Compare({
  before,
  row,
  n,
  approved,
  onApprove,
  onUnapprove,
  onClose,
}: {
  before: string | null;
  row: RenderResultRow;
  n: number;
  approved: boolean;
  onApprove: () => void;
  onUnapprove: () => void;
  onClose: () => void;
}) {
  return (
    <div role="dialog" aria-modal="true" aria-label={`Рендер ${n}: өмнө ба дараа`} className="fixed inset-0 z-50 flex flex-col gap-4 bg-[#0b0b0d] p-6 text-white">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg font-semibold">Рендер {n}</p>
          <p className="text-xs text-white/60">Зүүн талд эх зураг, баруун талд AI рендер. Барааны байрлал, хэмжээ нь зөвхөн чиглэл.</p>
        </div>
        {approved ? (
          <>
            <Badge variant="success" icon={Check}>
              Харилцагч баталсан
            </Badge>
            <button type="button" onClick={onUnapprove} className="h-11 rounded-lg border border-white/30 px-4 text-sm font-semibold">
              Баталгааг цуцлах
            </button>
          </>
        ) : (
          <button type="button" onClick={onApprove} className="inline-flex h-11 items-center gap-2 rounded-lg bg-white px-4 text-sm font-semibold text-[#111114]">
            <Check className="size-4" aria-hidden /> Харилцагч баталсан
          </button>
        )}
        <button type="button" onClick={onClose} aria-label="Хаах" className="grid size-11 place-items-center rounded-full border border-white/30">
          <X className="size-5" aria-hidden />
        </button>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-2 gap-4">
        {[
          { src: before, label: "Өмнө" },
          { src: row.output_url, label: "Дараа · AI" },
        ].map((p) => (
          <figure key={p.label} className="relative min-h-0 overflow-hidden">
            {p.src ? <img src={p.src} alt={p.label} className="size-full rounded-xl object-contain" /> : null}
            <figcaption className="absolute top-3 left-3 rounded-full bg-[#0b0b0d]/70 px-3 py-1 text-xs font-semibold">{p.label}</figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

export function RenderScreen() {
  const { id } = useParams();
  const project = useProject(id);
  const [selected, setSelected] = useState<number | null>(null);
  const [tab, setTab] = useState<"catalog" | "renders">("catalog");
  const [quality, setQuality] = useState<"preview" | "final">("preview");
  const [note, setNote] = useState("");
  const engines = useEngines();
  const [engineId, setEngineId] = useState<string | null>(null);
  const [resolution, setResolution] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [view, setView] = useState({ x: 0, y: 0, z: 1 });
  const pan = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);
  const base = project?.photos.find((p) => p.id === project.render.base_photo_id);
  const src = useBlobUrl(base);
  const projectId = project?.id;
  const runningIds = (project?.render.results ?? []).filter(isRunning).map((r) => r.id).join(",");

  // Fresh links for stored renders once (they expire), then follow running ones.
  const refreshed = useRef(false);
  useEffect(() => {
    if (!project || refreshed.current) return;
    refreshed.current = true;
    void refreshResults(project).catch(() => undefined);
  }, [project]);
  useEffect(() => {
    if (!projectId || !runningIds) return;
    const t = setInterval(() => {
      if (project) void refreshResults(project, runningIds.split(",")).catch(() => undefined);
    }, 1500);
    return () => clearInterval(t);
    // project changes on every poll; the ids decide what to follow
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, runningIds]);

  if (!project) return <Navigate to="/" replace />;
  const current = project;
  const items = project.render.items;
  const results = project.render.results;
  const aspect = photoAspect(base);
  const geo = layout(aspect, results.length);
  const total = items.reduce((s, it) => s + (it.price_mnt ?? 0), 0);
  const firstRoom = project.rooms[0];
  const ordered = [...results].reverse(); // oldest first: Рендер 1, 2, ...
  const openRow = ordered.find((r) => r.id === open);

  const change = (i: number, patch: Partial<RenderItemDraft>) =>
    void updateProject(current.id, (p) => {
      const it = p.render.items[i];
      if (it) Object.assign(it, patch);
    });
  const remove = (i: number) => {
    void updateProject(current.id, (p) => void p.render.items.splice(i, 1));
    setSelected(null);
  };
  const approve = (row: RenderResultRow) =>
    void updateProject(current.id, (p) => (p.render.approved = { result_id: row.id, approved_at: nowIso(), output_url: row.output_url }));

  async function addPhoto(file: File) {
    setBusy("Зураг бэлтгэж байна…");
    setError(null);
    try {
      const prepared = await preparePhoto(file);
      let photoId = "";
      await updateProject(current.id, (p) => {
        photoId = nextId(p, "P");
      });
      const ref = await storePhoto(prepared, { id: photoId, caption: "Render-ийн эх зураг" });
      await updateProject(current.id, (p) => {
        const same = p.photos.find((x) => x.sha256 === ref.sha256);
        if (!same) p.photos.push(ref);
        p.render.base_photo_id = same?.id ?? ref.id;
      });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  async function render() {
    if (!base || !items.length) return;
    setError(null);
    try {
      const engine = currentEngine(engines, engineId);
      await startRender(current, base, quality, note, setBusy, { engine: engine?.id ?? null, resolution: resolution && engine?.resolutions.includes(resolution) ? resolution : null });
      setSelected(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  const zoom = (dz: number) => setView((v) => ({ ...v, z: Math.min(1.6, Math.max(0.4, Math.round((v.z + dz) * 10) / 10)) }));
  const fit = () => {
    const z = Math.min(1, 700 / (geo.right + 24), 660 / (geo.bottom - 60));
    setView({ x: 0, y: 0, z: Math.round(z * 100) / 100 });
  };
  const startPan = (e: ReactPointerEvent<HTMLElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pan.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
    setSelected(null);
  };
  const movePan = (e: ReactPointerEvent) => {
    const p = pan.current;
    if (p) setView((v) => ({ ...v, x: p.vx + e.clientX - p.x, y: p.vy + e.clientY - p.y }));
  };

  const srcOut = { x: geo.src.x + geo.src.w, y: geo.src.y + HEAD + geo.src.imgH / 2 };
  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void addPhoto(f);
    e.target.value = "";
  };

  return (
    <div className="relative h-full overflow-hidden bg-background">
      <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" aria-label="Зураг авах" onChange={pickFile} />
      <input ref={library} type="file" accept="image/*" className="hidden" aria-label="Сангаас зураг сонгох" onChange={pickFile} />

      {/* The node canvas: source photo → renders. Drag the background to pan. */}
      <section
        aria-label="Студийн канвас"
        className="absolute inset-0 touch-none overflow-hidden bg-[radial-gradient(color-mix(in_srgb,var(--foreground)_14%,transparent)_1px,transparent_1px)] bg-size-[20px_20px]"
        onPointerDown={startPan}
        onPointerMove={movePan}
        onPointerUp={() => (pan.current = null)}
        onPointerCancel={() => (pan.current = null)}
      >
        <div className="absolute top-0 left-0 origin-top-left" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.z})` }}>
          <svg className="pointer-events-none absolute top-0 left-0 overflow-visible" width={1} height={1} aria-hidden>
            <defs>
              <marker id="arr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M0 0 L10 5 L0 10 z" className="fill-foreground/40" />
              </marker>
              <marker id="arr-run" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M0 0 L10 5 L0 10 z" className="fill-chart-1" />
              </marker>
            </defs>
            {ordered.map((r, i) => {
              const o = geo.outs[i];
              if (!o) return null;
              const end = { x: o.x, y: o.y + HEAD + o.imgH / 2 };
              const run = isRunning(r);
              return (
                <path
                  key={r.id}
                  d={`M${srcOut.x} ${srcOut.y} C${srcOut.x + 44} ${srcOut.y} ${end.x - 44} ${end.y} ${end.x - 2} ${end.y}`}
                  className={cn("fill-none stroke-2", run ? "stroke-chart-1 [stroke-dasharray:6_6]" : "stroke-foreground/40")}
                  markerEnd={run ? "url(#arr-run)" : "url(#arr)"}
                />
              );
            })}
          </svg>

          <div
            role="toolbar"
            aria-label="Эх зургийн үйлдэл"
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute flex gap-0.5 rounded-xl border bg-popover p-1 shadow-[0_8px_24px_#00000026]"
            style={{ left: LEFT, top: TOP - 50 }}
          >
            <button type="button" onClick={() => camera.current?.click()} className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium whitespace-nowrap hover:bg-accent">
              <Camera className="size-4" aria-hidden /> {base ? "Дахин авах" : "Зураг авах"}
            </button>
            <button type="button" onClick={() => library.current?.click()} className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium hover:bg-accent">
              <ImagePlus className="size-4" aria-hidden /> Сангаас
            </button>
            <button
              type="button"
              aria-pressed={tab === "catalog"}
              onClick={() => setTab("catalog")}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium aria-pressed:bg-foreground aria-pressed:text-background"
            >
              <Package className="size-4" aria-hidden /> Каталог
            </button>
          </div>

          <Node
            label="Эх зураг"
            icon={ImageIcon}
            title="Эх зураг"
            meta={base?.width_px ? `${base.width_px}×${base.height_px}` : undefined}
            outPort={results.length > 0}
            selected
            style={{ left: geo.src.x, top: geo.src.y, width: geo.src.w }}
          >
            <div className="mx-1.5" style={{ height: geo.src.imgH }}>
              {src ? (
                <PhotoCanvas src={src} aspect={aspect} items={items} selected={selected} onSelect={setSelected} onChange={change} className="h-full" />
              ) : (
                <div className="grid h-full place-content-center place-items-center gap-3 rounded-[9px] border border-dashed bg-muted/60 p-6 text-center">
                  <Camera className="size-7 text-muted-foreground" aria-hidden />
                  <p className="font-semibold">Өрөөний зургийг аваарай</p>
                  <p className="max-w-64 text-xs text-muted-foreground">Каталогийн барааг зураг дээр байрлуулж, AI-аар бодит харагдацыг гаргана.</p>
                  <button type="button" onClick={() => camera.current?.click()} className="inline-flex h-11 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground">
                    <Camera className="size-4" aria-hidden /> Зураг авах
                  </button>
                </div>
              )}
            </div>
            <div className="flex h-[50px] items-center justify-between gap-2 px-2.5 text-[11px] whitespace-nowrap text-muted-foreground">
              <div className="grid gap-0.5">
                <b className="text-xs font-semibold text-foreground">
                  {items.length} зүйл{total ? <span className="tabular"> · {formatMNT(total)}</span> : null}
                </b>
                <span>{total ? "Ойрын нийлүүлэгчийн үнээр" : "Каталогоос бараа нэмнэ"}</span>
              </div>
            </div>
          </Node>

          {ordered.map((r, i) => {
            const o = geo.outs[i];
            return o ? (
              <ResultNode
                key={r.id}
                row={r}
                n={i + 1}
                at={o}
                base={src}
                approved={project.render.approved?.result_id === r.id}
                onOpen={() => setOpen(r.id)}
                onApprove={() => approve(r)}
              />
            ) : null;
          })}
        </div>
      </section>

      {/* Top bar */}
      <div className="absolute inset-x-0 top-3 z-10 flex items-center gap-3 px-4 py-2">
        <Link to={`/p/${project.id}`} aria-label="Төсөл рүү буцах" className="grid size-10 place-items-center rounded-full border bg-card shadow-xs hover:bg-accent">
          <ChevronLeft className="size-5" aria-hidden />
        </Link>
        <div className="flex min-w-0 flex-col">
          <h1 className="truncate font-display text-[17px] leading-[22px] font-semibold tracking-[-0.02em]">{projectTitle(project)}</h1>
          <small className="truncate text-xs text-muted-foreground">
            Студи{project.site.address ? ` · ${project.site.address}` : ""} · {1 + results.length} зангилаа
          </small>
        </div>
        <span className="flex-1" />
        <div className="flex gap-0.5 rounded-full border bg-background/80 p-1 shadow-xs backdrop-blur-xl" role="group" aria-label="Студи">
          {firstRoom ? (
            <Link
              to={`/p/${project.id}/rooms/${firstRoom.id}?view=3d`}
              className="inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
            >
              <Box className="size-4" aria-hidden /> 3D байршуулалт
            </Link>
          ) : null}
          <span aria-current="page" className="inline-flex h-9 items-center gap-1.5 rounded-full bg-card px-3.5 text-[13px] font-semibold shadow-xs">
            <Sparkles className="size-4" aria-hidden /> Рендер
          </span>
        </div>
        <span className="flex-1" />
        {project.render.approved ? (
          <Badge variant="success" icon={Check}>
            Харилцагч баталсан
          </Badge>
        ) : (
          <span className="w-[140px]" />
        )}
      </div>

      {/* Zoom */}
      <div className="absolute bottom-24 left-4 z-10 flex items-center gap-0.5 rounded-xl border bg-background/80 p-1 backdrop-blur-xl" role="group" aria-label="Томруулах">
        <button type="button" aria-label="Жижигрүүлэх" onClick={() => zoom(-0.1)} className="grid size-9 place-items-center rounded-lg hover:bg-accent">
          <Minus className="size-4" aria-hidden />
        </button>
        <span className="min-w-11 text-center font-mono text-xs font-semibold">{Math.round(view.z * 100)}%</span>
        <button type="button" aria-label="Томруулах" onClick={() => zoom(0.1)} className="grid size-9 place-items-center rounded-lg hover:bg-accent">
          <Plus className="size-4" aria-hidden />
        </button>
        <button type="button" aria-label="Бүгдийг харуулах" onClick={fit} className="grid size-9 place-items-center rounded-lg hover:bg-accent">
          <Maximize className="size-4" aria-hidden />
        </button>
      </div>

      {/* Errors */}
      {error ? (
        <div className="absolute bottom-[156px] left-4 z-20 w-[706px]">
          {isSetupError(error) ? (
            <div className="relative">
              <SetupNotice code={(error as ApiError).code} />
              <button type="button" aria-label="Хаах" onClick={() => setError(null)} className="absolute top-2 right-2 grid size-9 place-items-center rounded-md hover:bg-accent">
                <X className="size-4" aria-hidden />
              </button>
            </div>
          ) : (
            <div className="relative">
              <Alert variant="destructive" className="bg-card pr-12 shadow-md">
                {error instanceof ApiError || error instanceof TypeError ? describeError(error) : error instanceof Error ? error.message : String(error)}
              </Alert>
              <button type="button" aria-label="Хаах" onClick={() => setError(null)} className="absolute top-1.5 right-1.5 grid size-9 place-items-center rounded-md hover:bg-accent">
                <X className="size-4" aria-hidden />
              </button>
            </div>
          )}
        </div>
      ) : null}

      {/* Render bar */}
      <div className="absolute bottom-4 left-4 z-10 flex w-[706px] items-center gap-2 rounded-2xl border bg-background/85 p-2 shadow-lg backdrop-blur-xl">
        {busy || !base || !items.length ? (
          <div className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl bg-muted px-3 text-sm text-muted-foreground">
            {busy ? <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden /> : <Images className="size-4 shrink-0" aria-hidden />}
            <span className="truncate" aria-live="polite">
              {busy ?? (!base ? "Эхлээд өрөөний зургийг аваарай" : "Каталогоос бараа нэмж зураг дээр байрлуулна уу")}
            </span>
          </div>
        ) : (
          <label className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl bg-muted px-3 text-sm text-muted-foreground">
            <MessageSquareText className="size-4 shrink-0" aria-hidden />
            <input
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void render();
              }}
              placeholder="Нэмэлт санаа бичих (заавал биш)…"
              aria-label="Render-ийн нэмэлт санаа"
              className="h-full min-w-0 flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
            />
          </label>
        )}
        {engines ? (
          <EnginePicker
            engines={engines}
            engineId={engineId}
            resolution={resolution}
            itemCount={items.length}
            onEngine={(id) => {
              setEngineId(id);
              setResolution(null);
            }}
            onResolution={setResolution}
            onChip={(text) => setNote((n) => (n.includes(text) ? n : `${n.trim() ? `${n.trim()}, ` : ""}${text}`).slice(0, 500))}
          />
        ) : null}
        <div className="flex h-11 items-center gap-0.5 rounded-xl border p-1" role="group" aria-label="Чанар">
          {(
            [
              ["preview", "Хурдан"],
              ["final", "Чанартай"],
            ] as const
          ).map(([q, label]) => (
            <button
              key={q}
              type="button"
              aria-pressed={quality === q}
              onClick={() => setQuality(q)}
              className={cn("inline-flex h-full items-center gap-1 rounded-lg px-2.5 text-[13px] font-semibold", quality === q ? "bg-foreground text-background" : "text-muted-foreground")}
            >
              {q === "preview" ? <Zap className="size-3.5" aria-hidden /> : null}
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={!base || !items.length || Boolean(busy)}
          onClick={() => void render()}
          className="inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-[18px] text-sm font-semibold whitespace-nowrap text-primary-foreground disabled:opacity-50"
        >
          <Sparkles className="size-4" aria-hidden /> Рендер хийх
        </button>
      </div>

      {/* Floating panel */}
      <aside
        aria-label="Студийн самбар"
        className="absolute top-[84px] right-4 bottom-4 z-10 flex w-[440px] flex-col gap-3 rounded-2xl border bg-background/88 px-[18px] py-4 shadow-lg backdrop-blur-xl"
      >
        <div className="flex gap-1 rounded-xl bg-muted p-1" role="tablist" aria-label="Самбарын горим">
          {(
            [
              ["catalog", "Каталог", Package],
              ["renders", `Рендерүүд${results.length ? ` · ${results.length}` : ""}`, Sparkles],
            ] as const
          ).map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={cn(
                "inline-flex h-[38px] flex-1 items-center justify-center gap-1.5 rounded-lg text-[13px] font-semibold",
                tab === key ? "bg-card shadow-xs" : "text-muted-foreground",
              )}
            >
              <Icon className="size-4" aria-hidden /> {label}
            </button>
          ))}
        </div>
        {tab === "catalog" ? (
          <>
            <CatalogTab
              project={project}
              disabled={!base || items.length >= MAX_ITEMS}
              onSelectPlaced={setSelected}
              onAdd={(a, offer) => {
                void updateProject(current.id, (p) => {
                  p.render.items.push(draftFromAsset(a, offer, p.render.items.length));
                });
                setSelected(items.length);
              }}
            />
            <PlacedList items={items} selected={selected} onSelect={setSelected} onChange={change} onRemove={remove} />
          </>
        ) : (
          <div className="grid min-h-0 auto-rows-min gap-2 overflow-y-auto">
            {!results.length ? <p className="text-sm text-muted-foreground">Рендер хийгээгүй байна. Бараагаа байрлуулаад «Рендер хийх» дарна уу.</p> : null}
            {results.map((r) => {
              const n = ordered.indexOf(r) + 1;
              const ok = r.status === "succeeded" && r.output_url;
              const approved = project.render.approved?.result_id === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  disabled={!ok}
                  onClick={() => setOpen(r.id)}
                  className={cn("flex items-center gap-3 rounded-xl border bg-card p-2 text-left", approved && "ring-2 ring-success")}
                >
                  <span className="grid h-14 w-20 shrink-0 place-items-center overflow-hidden rounded-lg bg-muted">
                    {ok ? <img src={r.output_url ?? ""} alt="" className="size-full object-cover" /> : isRunning(r) ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <TriangleAlert className="size-4 text-destructive" aria-hidden />}
                  </span>
                  <span className="grid min-w-0 flex-1 gap-0.5 text-xs">
                    <b className="text-sm font-semibold">Рендер {n}</b>
                    {r.note ? <span className="truncate">«{r.note}»</span> : null}
                    <span className="truncate text-muted-foreground">
                      {r.quality === "final" ? "Чанартай" : "Хурдан"} · {r.items ?? "?"} зүйл{r.engine ? ` · ${engineLabel(engines, r.engine)}${r.resolution ? ` ${r.resolution}` : ""}` : ""}
                      {isRunning(r) ? ` · ${r.progress ?? 0}%` : r.status === "failed" ? ` · ${failureText(r.error_code)}` : r.seconds ? ` · ${r.seconds} сек` : ""}
                    </span>
                  </span>
                  {approved ? (
                    <Badge variant="success" icon={Check}>
                      Батлагдсан
                    </Badge>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </aside>

      {openRow ? (
        <Compare
          before={src}
          row={openRow}
          n={ordered.indexOf(openRow) + 1}
          approved={project.render.approved?.result_id === openRow.id}
          onApprove={() => approve(openRow)}
          onUnapprove={() => void updateProject(current.id, (p) => (p.render.approved = null))}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

